'use client';

import { useContext, useState, useMemo, useCallback, FC } from 'react';
import {
  Button,
  ButtonGroup,
  CellActions,
  CellContainer,
  CellUserName,
  Dialog,
  Dropdown,
  Icon,
  Pill,
  ResultListItem,
  Text,
  Timestamp,
} from '@/_components';
import { getSignatureRequestStatus } from './SignatureRequestCell.utils';
import { SignatureRequestCellProps } from './SignatureRequestCell.types';
import { ToastContext } from '@/_contexts/ToastProvider';
import { downloadDocument } from '@/_api/admin-signature-requests';
import { UserContext } from '@/_contexts/UserProvider';
import { getDocumentName } from '@/_utils/files';
import { getClasses } from '@/_utils/classnames';

export const SignatureRequestCell: FC<SignatureRequestCellProps> = ({
  type,
  isLastColumn,
  signatureRequest,
  showSignatureRequest,
  updateSignatureRequest,
  deleteSignatureRequest,
  hasExtraPadding,
}) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { icon, text, intent } = getSignatureRequestStatus(signatureRequest);

  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);

  const handleSignatureRequestDeletion = useCallback(async () => {
    if (!signatureRequest || !deleteSignatureRequest) {
      return;
    }

    try {
      setIsLoading(true);
      await deleteSignatureRequest(
        signatureRequest.id,
        signatureRequest.company_id,
      );
      addToast('success', 'Signature request has been deleted');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsDeleteDialogOpen(false);
      setIsLoading(false);
    }
  }, [signatureRequest, deleteSignatureRequest, addToast]);

  const handleMarkAsCompleted = useCallback(async () => {
    if (!signatureRequest || !updateSignatureRequest) {
      return;
    }

    try {
      setIsLoading(true);
      await updateSignatureRequest(
        signatureRequest.id,
        signatureRequest.company_id,
        {
          status: 'completed',
        },
      );
      addToast('success', 'Signature request has been marked as completed');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
    }
  }, [signatureRequest, updateSignatureRequest, addToast]);

  const handleDownloadAllPages = useCallback(() => {
    const link = signatureRequest.signed_document
      ? signatureRequest.signed_document
      : signatureRequest.source;

    downloadDocument({
      companyId: signatureRequest.company_id,
      link,
      token,
    });
  }, [signatureRequest, token]);

  const handleDownloadSignedPagesOnly = useCallback(() => {
    if (!signatureRequest.signed_pages_only) return;

    downloadDocument({
      companyId: signatureRequest.company_id,
      link: signatureRequest.signed_pages_only,
      token,
    });
  }, [signatureRequest, token]);

  const documentName = useMemo(() => {
    if (signatureRequest.form_name) {
      return signatureRequest.form_name;
    } else {
      const source = signatureRequest.source;
      return getDocumentName(source);
    }
  }, [signatureRequest]);

  const getCellContent = () => {
    const actions: ResultListItem[] = [];
    const status = getSignatureRequestStatus(signatureRequest)?.value;

    actions.push({
      name: 'Download (all pages)',
      icon: 'download',
      onClick: () => {
        handleDownloadAllPages();
      },
    });

    if (signatureRequest.signed_pages_only) {
      actions.push({
        name: 'Download (signed pages only)',
        icon: 'download',
        onClick: () => {
          handleDownloadSignedPagesOnly();
        },
      });
    }

    if (status !== 'completed') {
      actions.push({
        name: 'Mark as completed',
        icon: 'check',
        onClick: () => {
          handleMarkAsCompleted();
        },
      });
    }

    if (deleteSignatureRequest) {
      actions.push({
        name: 'Delete',
        icon: 'trash',
        onClick: () => {
          setIsDeleteDialogOpen(true);
        },
      });
    }

    switch (type) {
      case 'company_name':
        return (
          <CellUserName
            companyHash={signatureRequest.company_hash}
            companyName={signatureRequest.company_name}
          />
        );
      case 'tax_year':
        return <CellContainer text={signatureRequest.tax_year} width={80} />;

      case 'document_name':
        return (
          <CellContainer width={240}>
            <a
              href="#"
              onClick={() => {
                if (showSignatureRequest) {
                  showSignatureRequest(signatureRequest.id);
                }
              }}
            >
              <Text type="tiny" lineClamp={2}>
                {documentName}
              </Text>
            </a>
          </CellContainer>
        );

      case 'status':
        return (
          <CellContainer>
            <Pill iconLeft={icon} label={text} intent={intent} />
          </CellContainer>
        );

      case 'updated_at':
        return (
          <CellContainer width={160}>
            <Text type="tiny" color="secondary">
              {!!signatureRequest.updated_at ? (
                <Timestamp timestamp={signatureRequest.updated_at} />
              ) : (
                <Icon size={16} name="minus" color="disabled" />
              )}
            </Text>
          </CellContainer>
        );

      case 'actions':
        return (
          <CellActions>
            <ButtonGroup>
              <Dropdown items={actions} placement="left">
                <Button
                  value="Actions"
                  size="small"
                  intent="ghost"
                  iconLeft="dots-three"
                  isIconOnly={true}
                />
              </Dropdown>
            </ButtonGroup>
          </CellActions>
        );
    }
  };

  const signatureCellClasses: string = getClasses({
    sorabanTable__fixedColumn: isLastColumn,
    extraPadding: hasExtraPadding,
  });

  return (
    <>
      <Dialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title="Are you sure you want to delete this tax return?"
      >
        <Dialog.Content>
          <ButtonGroup align="right">
            <Button
              intent="primary"
              isDisabled={isLoading}
              onClick={handleSignatureRequestDeletion}
              value="Yes"
            />

            <Button
              intent="danger-secondary"
              isDisabled={isLoading}
              onClick={() => setIsDeleteDialogOpen(false)}
              value="No"
            />
          </ButtonGroup>
        </Dialog.Content>
      </Dialog>
      <td className={signatureCellClasses}>{getCellContent()}</td>
    </>
  );
};
